import React, { MouseEventHandler, PropsWithChildren, ReactNode, useMemo } from 'react'
import clsx from 'clsx'
import { Card as CardStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /** card action area click handler */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /** If true, the card will get a focus effect */
  selected?: boolean
  /** Use this for a more affordant hover effect */
  translate?: boolean
}>

type DeprecatedProps = {
  color?: boolean
  elevation?: 1 | 2 | 4 | 8
}

export type CardProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: CardProps) => {
  const { elevation, color, ...rest } = props
  return rest
}

const useStyles = createUseStyles(CardStyles, 'card')

const Card = (props: CardProps) => {
  const { className, children, onClick, selected, translate, ...rest } = handleDeprecatedProps(props)
  useDeprecatedProps('Card', { elevation: undefined, color: undefined }, props)

  const classes = useStyles()

  const hasActionArea = useMemo(() => {
    let hasArea = false
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) {
        return null
      }
      if (child.type === CardActionArea) {
        hasArea = true
      }
    })
    return hasArea
  }, [children])

  const isActionable = onClick || hasActionArea
  const Wrapper = translate
    ? ({ children }: any) => <div className={classes.translate}>{children}</div>
    : React.Fragment

  return (
    <Wrapper>
      <div
        className={clsx(className, classes.root, isActionable && classes._actionable, selected && classes._selected)}
        {...rest}
      >
        {!onClick && children}
        {onClick && <CardActionArea onClick={onClick}>{children}</CardActionArea>}
      </div>
    </Wrapper>
  )
}

export default Card

export type CardChildProps = PropsWithChildren<CommonProps>

export const CardContent = (props: CardChildProps) => {
  const { className, children, ...rest } = props
  const classes = useStyles()
  return (
    <div className={clsx(className, classes.content)} {...rest}>
      {children}
    </div>
  )
}

export const CardTitle = (props: CardChildProps) => {
  const { className, children, ...rest } = props
  const classes = useStyles()
  return (
    <div className={clsx(className, classes.title)} {...rest}>
      {children}
    </div>
  )
}

export const CardActions = (props: CardChildProps) => {
  const { className, children, ...rest } = props
  const classes = useStyles()
  return (
    <div className={clsx(className, classes.actions)} {...rest}>
      {children}
    </div>
  )
}

type HeaderProps = {
  /** An IconButton to display on the right */
  action?: ReactNode
  /** An icon to display next to the title */
  icon?: ReactNode
  /** The title of the card */
  title?: string
}
export type CardHeaderProps = HeaderProps & CommonProps

export const CardHeader = (props: CardHeaderProps) => {
  const { title, icon, action, className, ...rest } = props
  const classes = useStyles()
  return (
    <div className={clsx(className, classes.header)} {...rest}>
      {icon && <div className={classes.headerIcon}>{icon}</div>}
      <div className={classes.headerTitle}>{title}</div>
      {action && <div className={classes.headerAction}>{action}</div>}
    </div>
  )
}

type MediaProps = {
  /** The image to display */
  image?: string
  /** The alt title of the image */
  title?: string
}
export type CardMediaProps = MediaProps & CommonProps

export const CardMedia = (props: CardMediaProps) => {
  const { image, title, className, style, ...rest } = props
  const classes = useStyles()
  return (
    <div
      className={clsx(className, classes.media)}
      style={{ ...style, backgroundImage: `url(${image})` }}
      title={title}
      {...rest}
    />
  )
}

export type CardActionAreaProps = PropsWithChildren<CommonProps>

export const CardActionArea = (props: CardActionAreaProps) => {
  const { className, children, ...rest } = props
  const classes = useStyles()
  return (
    <button type='button' className={clsx(className, classes.actionArea)} {...rest}>
      {children}
    </button>
  )
}
